import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Button, FormControl, IconButton, MenuItem, Select, Stack, Switch, TextField } from "@mui/material";

import { api } from "../../../../../utils";
import { IError } from "../../../../../models/ReportPageModels";

interface AdminIccBranchesResponse {
    data: IccBranchesData;
    error: IError;
}
interface IccBranchesData {
    branches: DbBranchModel[];
}

interface DbBranchModel {
    id: number;
    branchName: string;
    databaseName: string;
    logosource: string;
    enabled: number;
}

interface GetDatabasesResponse {
    data: GetDatabasesData;
    error: IError;
}
interface GetDatabasesData {
    databases: string[];
}

interface EditIccBranchRequest {
    id: number;
    branchName: string;
    database: string;
    logoUrl: string;
    enabled: boolean;
}
interface EditIccBranchResponse {
    data: EditIccBranchData;
    error: IError;
}
interface EditIccBranchData {
    status: string;
}

function EditIccBranch() {
    const { enqueueSnackbar } = useSnackbar();

    const [branchId, setBranchId] = useState<number>(0);
    const [branchName, setBranchName] = useState<string>("");
    const [selectedDatabase, setSelectedDatabase] = useState<string>("");
    const [logoUrl, setLogoUrl] = useState<string>("");
    const [enabled, setEnabled] = useState<boolean>(true);

    const [branches, setBranches] = useState<DbBranchModel[]>([]);
    const [databases, setDatabases] = useState<string[]>([]);

    useEffect(() => {
        GetBranches();
        GetDatabases();
    }, []);

    const GetBranches = async () => {
        try {
            const response: AdminIccBranchesResponse = await api.get("api/Admin/GetICCBranches", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setBranches(response.data.branches)
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    };

    const GetDatabases = async () => {
        try {
            const response: GetDatabasesResponse = await api.get("api/Admin/GetDatabases", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }
            ).json();

            if (response.error == null) {
                setDatabases(response.data.databases)
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    };

    function SetBranchId(event: any) {
        var value: number = event.target.value;
        setBranchId(value);

        if (value == 0) {
            setBranchName("");
            setSelectedDatabase("");
            setLogoUrl("");
            setEnabled(true);
            return;
        }

        var branch = null;

        for (var i = 0; i < branches.length; i++) {
            if (branches[i].id === value) {
                branch = branches[i]
                break;
            }
        }

        if (branch === null)
            return;

        setBranchName(branch.branchName);
        setSelectedDatabase(branch.databaseName);
        setLogoUrl(branch.logosource);
        setEnabled(branch.enabled == 1);
    }

    const EditBranch = async () => {
        if (branchId === 0 || branchName === "" || selectedDatabase === "") {
            enqueueSnackbar("Please recheck fields", { variant: "error" });
            return;
        }

        var body: EditIccBranchRequest = {
            id: branchId,
            branchName: branchName,
            database: selectedDatabase,
            logoUrl: logoUrl,
            enabled: enabled
        }

        try {
            const response: EditIccBranchResponse = await api.post("api/Admin/EditIccBranch", {
                json: body,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                enqueueSnackbar("Edited!", { variant: "success" })
                // clear fields
                setBranchId(0);
                setBranchName("");
                setSelectedDatabase("");
                setLogoUrl("");
                setEnabled(true);

                // get new users from db
                GetBranches();
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const CopyDatabaseToClipboard = async () => {
        if (selectedDatabase === "") return;
        enqueueSnackbar("Copied!", { variant: "success" });
        await navigator.clipboard.writeText(selectedDatabase);
    }

    const IsFormValid = branchId !== 0 && branchName !== "" && selectedDatabase !== "";

    return (
        <Stack>
            {/* BranchId */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Branches: </p>
                <FormControl sx={{ minWidth: 245 }}>
                    <Select
                        value={branchId}
                        onChange={SetBranchId}
                        size="small"
                        defaultValue={-1}>
                        <MenuItem
                            value={0}>
                            {"Select branch"}
                        </MenuItem>
                        {branches.map((branch, index) =>
                            <MenuItem
                                key={index}
                                value={branch.id}>
                                {branch.branchName}
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
            </Stack>

            {/* BranchName */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Branch name: </p>
                <TextField
                    value={branchName}
                    onChange={(newValue: any) => { setBranchName(newValue.target.value) }}
                    sx={{ minWidth: 245 }}
                    size="small" />
            </Stack>

            {/* Database */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Database: </p>
                <FormControl sx={{ minWidth: 245 }}>
                    <Select
                        value={selectedDatabase}
                        onChange={(newValue: any) => { setSelectedDatabase(newValue.target.value); }}
                        size="small"
                        defaultValue={""}>
                        <MenuItem
                            value={""}>
                            {"Select database"}
                        </MenuItem>
                        {databases.map((database: string, index) =>
                            <MenuItem
                                key={index}
                                value={database}>
                                {database}
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
                <IconButton onClick={CopyDatabaseToClipboard}>
                    <ContentCopyIcon />
                </IconButton>
            </Stack>

            {/* Logo */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Logo url: </p>
                <TextField
                    value={logoUrl}
                    onChange={(newValue: any) => { setLogoUrl(newValue.target.value) }}
                    sx={{ minWidth: 400 }}
                    size="small" />
            </Stack>

            {/* Enabled */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Enabled: </p>
                <Switch
                    checked={enabled}
                    onChange={(newValue: any) => { setEnabled(newValue.target.checked) }}
                    size="small" />
            </Stack>

            <Button
                variant="outlined"
                onClick={() => EditBranch()}
                sx={{ width: '82px' }}
                disabled={!IsFormValid}>
                Edit
            </Button>
        </Stack>
    )
}

export default EditIccBranch;